// Audio play button + editable speaker label for meeting utterances.
//
// Talks to Bobbin's localhost HTTP API (127.0.0.1:8770) rather than the
// Thread cloud — works only when the user is on the same Mac as Bobbin's
// sidecar. Failure shows an inline message; doesn't break the rest of
// the viewer.

import { useEffect, useRef, useState } from "react";
import type { Turn, MeetingTurnMeta } from "./types";
import {
  BobbinUnavailableError,
  fetchUtteranceAudioBlobUrl,
  patchUtteranceSpeaker,
} from "./bobbinLocal";

interface Props {
  turn: Turn;
}

export function MeetingTurnExtras({ turn }: Props): React.ReactElement {
  const meta = (turn.metadata ?? {}) as MeetingTurnMeta & {
    speaker_resolved?: string;
    source_track?: string;
  };
  const displaySpeaker =
    (meta.speaker_resolved as string | undefined) ||
    turn.speaker_label ||
    (meta.track as string | undefined) ||
    "(unknown)";

  // ---- Speaker label edit ------------------------------------------------
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displaySpeaker);
  const [savedLabel, setSavedLabel] = useState<string>(displaySpeaker);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setSavedLabel(displaySpeaker);
    setDraft(displaySpeaker);
  }, [displaySpeaker]);

  async function commit() {
    if (draft === savedLabel) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const next = draft.trim();
      const out = await patchUtteranceSpeaker(
        turn.source_uri,
        next.length === 0 ? null : next,
      );
      setSavedLabel(out.speaker_resolved || out.speaker || "(unknown)");
      setEditing(false);
    } catch (e) {
      setSaveError(
        e instanceof BobbinUnavailableError
          ? "Bobbin not reachable."
          : (e as Error).message,
      );
    } finally {
      setSaving(false);
    }
  }

  // ---- Audio playback ----------------------------------------------------
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [audioState, setAudioState] = useState<
    "idle" | "loading" | "playing" | "paused" | "error"
  >("idle");
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  async function togglePlay() {
    if (audioState === "playing" && audioRef.current) {
      audioRef.current.pause();
      setAudioState("paused");
      return;
    }
    if (audioState === "paused" && audioRef.current) {
      audioRef.current.play();
      setAudioState("playing");
      return;
    }
    setAudioState("loading");
    setAudioError(null);
    try {
      const track =
        (meta.source_track as string | undefined) ?? meta.track ?? null;
      const url = await fetchUtteranceAudioBlobUrl(turn.source_uri, track);
      blobUrlRef.current = url;
      const el = new Audio(url);
      audioRef.current = el;
      const startSec = ((meta.start_ms as number | undefined) ?? 0) / 1000;
      el.currentTime = startSec;
      const endSec = ((meta.end_ms as number | undefined) ?? null) ;
      if (endSec !== null) {
        const stop = () => {
          if (el.currentTime >= endSec / 1000) {
            el.pause();
            setAudioState("paused");
            el.removeEventListener("timeupdate", stop);
          }
        };
        el.addEventListener("timeupdate", stop);
      }
      el.addEventListener("ended", () => setAudioState("paused"));
      el.addEventListener("error", () => {
        setAudioState("error");
        setAudioError("playback error");
      });
      await el.play();
      setAudioState("playing");
    } catch (e) {
      setAudioState("error");
      setAudioError(
        e instanceof BobbinUnavailableError
          ? "Bobbin not reachable — sidecar must be running for playback."
          : (e as Error).message,
      );
    }
  }

  const playLabel =
    audioState === "loading"
      ? "…"
      : audioState === "playing"
        ? "⏸"
        : audioState === "error"
          ? "!"
          : "▶";

  return (
    <div className="cv-meeting-extras">
      <button
        type="button"
        className="cv-play-btn"
        title="Play this utterance"
        onClick={togglePlay}
        disabled={audioState === "loading"}
      >
        {playLabel}
      </button>
      {editing ? (
        <input
          autoFocus
          className="cv-speaker-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            else if (e.key === "Escape") {
              setDraft(savedLabel);
              setEditing(false);
            }
          }}
          disabled={saving}
        />
      ) : (
        <button
          type="button"
          className="cv-speaker-edit"
          onClick={() => {
            setDraft(savedLabel);
            setEditing(true);
          }}
          title="Click to correct speaker"
        >
          {savedLabel}
        </button>
      )}
      {saveError && <span className="cv-save-error">{saveError}</span>}
      {audioError && <span className="cv-save-error">{audioError}</span>}
    </div>
  );
}
